<template>
  <BModal
    ref="change-password-modal"
    :title="$t('app.settings.changePassword')"
    header-text-variant="light"
    header-class="border-bottom-0"
    header-bg-variant="warning"
    footer-class="border-top-0 pt-0"
    centered
    no-close-on-backdrop
    no-close-on-esc
    @ok.prevent="onSubmit"
  >
    <p v-if="openedFromPasswordWarning" class="mb-4" style="font-size: medium">
      {{ $t('app.settings.passwordChangeWarning') }}
    </p>
    <BAlert variant="warning" class="mb-4 d-flex align-items-center" show>
      <i class="fas fa-exclamation-triangle mr-2 flex-shrink-0" />
      {{ $t('app.common.invalid.password') }}
    </BAlert>
    <BForm @submit.prevent="onSubmit">
      <BRow>
        <BCol cols="12">
          <BFormGroup :label="$t('app.users.labels.currentPassword')">
            <b-input-group>
              <BFormInput
                v-model="currentPassword"
                v-validate="'required'"
                :state="errors.has('currentPassword') ? false : null"
                :placeholder="$t('app.users.placeholders.currentPassword')"
                :type="showCurrentPassword ? 'text' : 'password'"
                name="currentPassword"
                autofocus
              />
              <b-input-group-append>
                <b-input-group-text
                  class="px-3"
                  style="cursor: pointer"
                  tabindex="-1"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  <b-icon :icon="showCurrentPassword ? 'eye-slash' : 'eye'" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <BFormInvalidFeedback
              :state="errors.has('currentPassword') ? false : true"
            >
              {{ errors.first('currentPassword') }}
            </BFormInvalidFeedback>
          </BFormGroup>
        </BCol>

        <BCol cols="12">
          <BFormGroup :label="$t('app.users.labels.newPassword')">
            <b-input-group>
              <BFormInput
                v-model="password"
                v-validate="'required|validPassword'"
                :state="errors.has('password') ? false : null"
                :placeholder="$t('app.users.placeholders.password')"
                :type="showNewPassword ? 'text' : 'password'"
                ref="password"
                name="password"
              />
              <b-input-group-append>
                <b-input-group-text
                  class="px-3"
                  style="cursor: pointer"
                  tabindex="-1"
                  @click="showNewPassword = !showNewPassword"
                >
                  <b-icon :icon="showNewPassword ? 'eye-slash' : 'eye'" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <BFormInvalidFeedback
              :state="errors.has('password') ? false : true"
            >
              {{ errors.first('password') }}
            </BFormInvalidFeedback>
          </BFormGroup>
        </BCol>

        <BCol cols="12">
          <BFormGroup :label="$t('app.users.labels.newRePassword')">
            <b-input-group>
              <BFormInput
                v-model="repeatPassword"
                v-validate="{ required: true, is: password }"
                :state="errors.has('repeatPassword') ? false : null"
                :placeholder="$t('app.users.placeholders.rePassword')"
                :type="showRepeatPassword ? 'text' : 'password'"
                name="repeatPassword"
              />
              <b-input-group-append>
                <b-input-group-text
                  class="px-3"
                  style="cursor: pointer"
                  tabindex="-1"
                  @click="showRepeatPassword = !showRepeatPassword"
                >
                  <b-icon :icon="showRepeatPassword ? 'eye-slash' : 'eye'" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <BFormInvalidFeedback
              :state="errors.has('repeatPassword') ? false : true"
            >
              {{ errors.first('repeatPassword') }}
            </BFormInvalidFeedback>
          </BFormGroup>
        </BCol>
      </BRow>
    </BForm>
    <BAlert :show="!!error" variant="danger" class="mb-0">{{ error }}</BAlert>
    <template #modal-footer>
      <b-button
        v-if="openedFromPasswordWarning"
        variant="light"
        class="text-dark"
        @click="onRemindLater"
      >
        {{ $t('app.settings.passwordChangeRemindLater') }}
      </b-button>
      <b-button variant="secondary" @click="hideModal">
        {{ $t('app.forms.close') }}
      </b-button>
      <b-button variant="warning" @click="onSubmit">
        {{ $t('app.settings.change') }}
      </b-button>
    </template>
  </BModal>
</template>

<script>
import { Validator, Rules } from 'vee-validate'
import validationRules from '~/components/utils/validationRules'

export default {
  props: {
    superLog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openedFromPasswordWarning: false,
      error: null,
      currentPassword: null,
      password: null,
      repeatPassword: null,
      showCurrentPassword: false,
      showNewPassword: false,
      showRepeatPassword: false,
    }
  },
  mounted() {
    Validator.extend('validPassword', {
      getMessage: () => this.$t('app.common.invalid.password'),
      validate: (value) => validationRules.validatePassword(value),
    })
    Validator.extend('is', {
      ...Rules.is,
      getMessage: () => this.$t('app.common.invalid.is'),
    })
  },
  methods: {
    showModal(fromPasswordWarning = false) {
      this.openedFromPasswordWarning = fromPasswordWarning
      this.error = null
      this.currentPassword = null
      this.password = null
      this.repeatPassword = null
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showRepeatPassword = false

      this.$refs['change-password-modal'].show()
    },
    hideModal() {
      this.$refs['change-password-modal'].hide()
    },
    onRemindLater() {
      this.$emit('dismiss')
      this.hideModal()
    },
    onSubmit() {
      this.error = null

      this.$validator.validate().then(async (valid) => {
        if (valid) {
          try {
            this.$nuxt.$loading.start()

            await this.$axios.post(
              '/akern-platform-backend/v1/customer/users/update-password',
              {
                accessToken: this.$store.getters['user/token'],
                currentPassword: this.currentPassword,
                newPassword: this.password,
                superLog: this.superLog,
              }
            )

            this.$bvToast.toast(
              this.$t('app.settings.passwordSuccessfullyChanged'),
              {
                title: this.$t('app.settings.changePassword'),
                variant: 'success',
              }
            )

            this.$store.dispatch('user/invalidateUser')
            await this.$store.dispatch('user/fetchUser')
            this.$refs['change-password-modal'].hide()
          } catch (error) {
            const code = this.$getErrorMessage(error)
            const key = `app.settings.${code}`
            this.error = this.$te(key) ? this.$t(key) : code
          } finally {
            this.$nuxt.$loading.finish()
          }
        }
      })
    },
  },
}
</script>
